import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Welcome popup, rendered globally (App.js) so it appears on whatever page the
// user lands on after auth. Two triggers (localStorage flags):
//   • "showWelcomeCard" = "1"  → set on SIGNUP  → role-based welcome.
//   • "showWelcomeBack" = "1"  → set on LOGIN   → simple "Welcome Back".
// Manual close ONLY — no auto-timeout; stays until the user clicks ✕ / a button.
// Never pops on auth/verification pages (login, register, otp, 2fa).

const AUTH_PATHS = ["/login", "/register", "/signup", "/otp", "/2fa", "/auth", "/forgot", "/reset"];

const SUPPORT_WHATSAPP = "923411228760"; // wa.me number; displayed as 03411228760

const ROLE_CONTENT = {
  expert: {
    badge: "Expert / freelancer",
    accent: "#5BA3F5",
    accentBg: "rgba(91,163,245,0.13)",
    intro: "You're all set as an expert. To stand out and win more orders:",
    bullets: [
      "Create at least 5 gigs in your category.",
      "Focus on different keywords across your gigs so you show up in more searches.",
      "Add strong portfolios and clear packages.",
      "Browse buyer requests from your Expert account and send strong, tailored proposals.",
      "Win the project and deliver high-quality work to your client to increase the chances of getting hired again.",
      "If you're a professional-level expert in any skill, we recommend creating both an Expert and a Business Developer account.",
      "Create at least 5 gigs on each of your Expert and Business Developer accounts to maximize your visibility and increase your chances of winning more projects.",
    ],
    cta: "Create my first gig",
    to: "/freelancers",
  },
  bd: {
    badge: "Business developer",
    accent: "#F5B544",
    accentBg: "rgba(245,181,68,0.14)",
    intro: "You connect clients with the right experts. To succeed:",
    bullets: [
      "Create at least 5 gigs in your category.",
      "Focus on different keywords across your gigs so you show up in more searches.",
      "Add strong portfolios and clear packages.",
      "Browse buyer requests and send strong, tailored proposals.",
      "Win the project, then assign the best expert from our talent pool.",
      "Spend your bids wisely — target the requests that fit your experts.",
    ],
    cta: "Browse buyer requests",
    to: "/buyerRequest",
  },
  client: {
    badge: "Client",
    accent: "#9C8BFF",
    accentBg: "rgba(156,139,255,0.16)",
    intro: "You can hire freelancers in two ways:",
    numbered: [
      "Browse & Order — Search for a service, explore freelancer profiles, compare portfolios, reviews, pricing, and delivery time, then place your order with confidence.",
      "Post a Job / Create Buyer Request — Describe your project requirements, receive proposals from skilled freelancers, compare offers, communicate with experts, and hire the best freelancer for your needs.",
    ],
    note: "GrapeTask keeps your payment secure and does not release it to the freelancer until the work is successfully delivered. GrapeTask does not charge any hidden fees from clients.",
    cta: "Get started",
    to: "/category-wise-gigs",
  },
};

const detectRole = () => {
  let raw = "";
  try {
    raw = JSON.parse(localStorage.getItem("UserData") || "{}").role || "";
  } catch (e) {
    raw = "";
  }
  raw = (raw || localStorage.getItem("Role") || "").toLowerCase();
  if (raw.includes("expert") || raw.includes("freelancer") || raw === "seller") return "expert";
  if (
    raw.includes("bidder") ||
    raw.includes("middleman") ||
    raw.includes("company representative") ||
    raw === "bd" ||
    (raw.includes("business") && raw.includes("developer"))
  )
    return "bd";
  return "client";
};

const WelcomeCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState("signup"); // 'signup' | 'login'
  const [role, setRole] = useState("client");

  // Re-check on every route change so the popup appears on the FIRST real page
  // the user lands on after signup/login (App.js keeps this mounted across nav).
  useEffect(() => {
    const path = (location.pathname || "").toLowerCase();
    if (AUTH_PATHS.some((p) => path.startsWith(p))) return; // skip auth/verify pages
    if (localStorage.getItem("showWelcomeBack") === "1") {
      localStorage.removeItem("showWelcomeBack"); // clear now → only manual close hides it
      setMode("login");
      setRole(detectRole()); // show the role-specific guidance on login too
      setShow(true);
    } else if (localStorage.getItem("showWelcomeCard") === "1") {
      localStorage.removeItem("showWelcomeCard");
      setMode("signup");
      setRole(detectRole());
      setShow(true);
    }
  }, [location.pathname]);

  const close = () => setShow(false); // manual close only — no auto-timeout
  const onCta = (to) => { close(); navigate(to); };

  if (!show) return null;

  const isLogin = mode === "login";
  const c = ROLE_CONTENT[role] || ROLE_CONTENT.client;

  return (
    <div onClick={close} style={OVERLAY}>
      <style>{CSS}</style>
      <div className="gt-welc" onClick={(e) => e.stopPropagation()}>
        {/* Header — GrapeTask ● Live */}
        <div className="gt-welc-head">
          <div className="gt-welc-title">
            GrapeTask <span className="gt-welc-dot" /> <span className="gt-welc-live">Live</span>
          </div>
          <button onClick={close} aria-label="Close" className="gt-welc-x">×</button>
        </div>

        <div className="gt-welc-body">
          <h4 className="gt-welc-h">{isLogin ? "Welcome Back 👋" : "Welcome to GrapeTask"}</h4>
          <p className="gt-welc-sub">{isLogin ? "GrapeTask — Pakistan's No.1 Freelance Marketplace" : "Pakistan's No.1 Freelance Marketplace"}</p>
          <p className="gt-welc-text">{c.intro}</p>

          <div className="gt-welc-list">
            {(c.bullets || []).map((b, i) => (
              <div key={i} className="gt-welc-row"><span className="gt-welc-tick">✓</span><span>{b}</span></div>
            ))}
            {(c.numbered || []).map((b, i) => (
              <div key={i} className="gt-welc-row"><span className="gt-welc-tick">{i + 1}</span><span>{b}</span></div>
            ))}
          </div>

          {c.note && <p className="gt-welc-note">{c.note}</p>}

          <p className="gt-welc-support">
            Need Guidance? WhatsApp GrapeTask Customer Support:{" "}
            <a href={`https://wa.me/${SUPPORT_WHATSAPP}`} target="_blank" rel="noopener noreferrer">03411228760</a>
          </p>

          {isLogin ? (
            <button className="gt-welc-btn" onClick={close}>Continue</button>
          ) : (
            <>
              <button className="gt-welc-btn" onClick={() => onCta(c.to)}>{c.cta}</button>
              <div className="gt-welc-skip" onClick={close}>Maybe later</div>
            </>
          )}

          <div className="gt-welc-foot">Version 2.0.4 • Optimized for Experts &amp; Bidders</div>
        </div>
      </div>
    </div>
  );
};

const OVERLAY = { position: "fixed", inset: 0, background: "rgba(2,6,23,0.80)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 };

// Matches the "GrapeTask Live" modal design (dark #020617 card, Live header with
// pulsing dot, feature gradient button, version footer). Scoped under .gt-welc.
const CSS = `
  .gt-welc { width:100%; max-width:520px; max-height:90vh; overflow-y:auto; overflow-x:hidden; background:#020617; border:1px solid rgba(255,255,255,0.07); border-radius:24px; position:relative; box-shadow:0 0 40px rgba(0,0,0,0.6), 0 0 20px rgba(240,89,31,0.05); }
  .gt-welc * { max-width:100%; box-sizing:border-box; }
  .gt-welc::after { content:""; position:absolute; top:-50px; right:-50px; width:150px; height:150px; background:#f0591f; filter:blur(80px); opacity:0.1; pointer-events:none; }
  .gt-welc-head { display:flex; align-items:center; justify-content:space-between; padding:1.25rem 1.8rem; border-bottom:1px solid rgba(255,255,255,0.07); background:linear-gradient(to bottom, rgba(255,255,255,0.02), transparent); }
  .gt-welc-title { color:#fff; font-size:1.35rem; font-weight:800; letter-spacing:-0.03em; display:flex; align-items:center; gap:9px; }
  .gt-welc-dot { width:8px; height:8px; background:#22c55e; border-radius:50%; display:inline-block; box-shadow:0 0 10px #22c55e; animation:gtPulse 2s infinite; }
  .gt-welc-live { color:#f0591f; margin-left:-3px; }
  @keyframes gtPulse { 0%{transform:scale(.95);box-shadow:0 0 0 0 rgba(34,197,94,.7);} 70%{transform:scale(1);box-shadow:0 0 0 6px rgba(34,197,94,0);} 100%{transform:scale(.95);box-shadow:0 0 0 0 rgba(34,197,94,0);} }
  .gt-welc-x { background:transparent; border:none; color:#a1a1aa; font-size:1.7rem; line-height:1; cursor:pointer; opacity:.6; transition:all .3s; }
  .gt-welc-x:hover { opacity:1; transform:rotate(90deg); }
  .gt-welc-body { padding:1.9rem 1.8rem 1.5rem; }
  .gt-welc-h { color:#fff; font-size:1.5rem; font-weight:700; letter-spacing:-0.02em; margin:0 0 4px; }
  .gt-welc-sub { color:#f0591f; font-size:0.8rem; font-weight:600; letter-spacing:.3px; margin:0 0 14px; }
  .gt-welc-text { color:#a1a1aa; font-size:0.95rem; line-height:1.7; margin:0; }
  .gt-welc-list { margin:14px 0 0; }
  .gt-welc-row { display:flex; gap:10px; align-items:flex-start; margin:10px 0; color:#c7c7d1; font-size:0.92rem; line-height:1.55; }
  .gt-welc-tick { color:#f0591f; font-weight:700; min-width:16px; }
  .gt-welc-note { margin:16px 0 0; padding:11px 13px; background:rgba(240,89,31,0.08); border:1px solid rgba(240,89,31,0.22); border-radius:10px; color:#c7c7d1; font-size:0.83rem; line-height:1.55; }
  .gt-welc-support { margin:16px 0 0; color:#a1a1aa; font-size:0.82rem; line-height:1.5; }
  .gt-welc-support a { color:#22c55e; font-weight:600; text-decoration:none; }
  .gt-welc-btn { margin-top:18px; background:linear-gradient(135deg,#f0591f 0%,#d94e18 100%); color:#fff; border:none; width:100%; padding:14px; border-radius:12px; font-weight:700; font-size:1rem; cursor:pointer; box-shadow:0 8px 20px rgba(240,89,31,0.2); transition:all .3s cubic-bezier(0.4,0,0.2,1); }
  .gt-welc-btn:hover { transform:translateY(-2px); box-shadow:0 12px 25px rgba(240,89,31,0.3); filter:brightness(1.1); }
  .gt-welc-skip { text-align:center; margin-top:12px; color:#71717a; font-size:0.85rem; cursor:pointer; }
  .gt-welc-foot { text-align:center; margin-top:1.2rem; font-size:0.72rem; color:#a1a1aa; opacity:0.6; }
`;

export default WelcomeCard;
